import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { createLogger } from '~/utils/logging';
import { PERIOD_LIST_ALL_QUERY_KEY, PERIOD_QUERY_KEY } from '~/services/period';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';

type PeriodRecord = Record<string, unknown> & { id: string };
type PeriodListData = { periods: PeriodRecord[] };

const log = createLogger('usePeriodArchiveMutation');

export type ArchivePeriodRequest = {
  periodId: string;
};

export type ArchivePeriodResponse = {
  periodId: string;
};

export function usePeriodArchiveMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: ArchivePeriodRequest) => {
      log.debug('Archiving period', { request });
      const { periodId } = await httpClient.post<ArchivePeriodResponse>('/period/archive', request);
      if (periodId) {
        log.info('Archived period successfully', { periodId });
      }
      return { periodId };
    },
    onMutate: async (request: ArchivePeriodRequest) => {
      const { periodId } = request;

      // Cancel any outgoing refetches to prevent them from overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
      await queryClient.cancelQueries({ queryKey: [PERIOD_QUERY_KEY, periodId] });

      // Snapshot the previous values
      const previousActivePeriodsWithStudents = queryClient.getQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, false]);
      const previousArchivedPeriodsWithStudents = queryClient.getQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, true]);
      const previousPeriodWithStudents = queryClient.getQueryData([PERIOD_QUERY_KEY, periodId, true]);

      const archivedAt = new Date().toISOString();

      // Optimistically update active periods list (remove the archived period)
      queryClient.setQueryData<PeriodListData>([PERIOD_LIST_ALL_QUERY_KEY, true, false], (old) => {
        if (!old?.periods) return old;
        return {
          ...old,
          periods: old.periods.filter((period) => period.id !== periodId),
        };
      });

      // Optimistically update archived periods list (add the period with archivedAt)
      queryClient.setQueryData<PeriodListData>([PERIOD_LIST_ALL_QUERY_KEY, true, true], (old) => {
        if (!old?.periods) return old;

        // Find the period in the current list or from the active list snapshot
        const periodToArchive =
          old.periods.find((p) => p.id === periodId) ||
          (previousActivePeriodsWithStudents as PeriodListData | undefined)?.periods?.find((p) => p.id === periodId);

        if (!periodToArchive) return old;

        // Check if period already exists in archived list
        const existsInArchived = old.periods.some((p) => p.id === periodId);

        if (existsInArchived) {
          // Update existing period
          return {
            ...old,
            periods: old.periods.map((p) => (p.id === periodId ? { ...p, archivedAt } : p)),
          };
        } else {
          // Add period to archived list
          return {
            ...old,
            periods: [...old.periods, { ...periodToArchive, archivedAt }],
          };
        }
      });

      // Optimistically update single period query (set archivedAt)
      queryClient.setQueryData<PeriodRecord>([PERIOD_QUERY_KEY, periodId, true], (old) => {
        if (!old) return old;
        return { ...old, archivedAt };
      });

      // Return context object with snapshotted values for rollback
      return {
        previousActivePeriodsWithStudents,
        previousArchivedPeriodsWithStudents,
        previousPeriodWithStudents,
        periodId,
      };
    },
    onError: (error: Error, _request, context) => {
      // Rollback optimistic updates on error
      if (context) {
        queryClient.setQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, false], context.previousActivePeriodsWithStudents);
        queryClient.setQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, true], context.previousArchivedPeriodsWithStudents);
        queryClient.setQueryData([PERIOD_QUERY_KEY, context.periodId, true], context.previousPeriodWithStudents);
      }

      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to archive period', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
    onSettled: (_data, _error, request) => {
      // Always refetch after error or success to ensure server state is correct
      queryClient.invalidateQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PERIOD_QUERY_KEY, request.periodId] });
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
